import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import Keycloak from 'keycloak-js';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import axios from 'axios';

// Keycloak configuration from environment variables
const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL;
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM;
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;
const KEYCLOAK_REDIRECT_URI = import.meta.env.VITE_KEYCLOAK_REDIRECT_URI || window.location.origin;

// Add logout URL from environment variables
const KEYCLOAK_LOGOUT_URL = import.meta.env.VITE_KEYCLOAK_LOGOUT_URL;

// Add chat API URL from environment variables
const CHAT_API_URL = import.meta.env.VITE_CHAT_API_URL;


type KeycloakContextType = {
  user: any;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: () => void;
  signUp: () => void;
  signOut: () => Promise<void>;
};

const KeycloakContext = createContext<KeycloakContextType | undefined>(undefined);

// Main Context Provider that will be used throughout the app
export const KeycloakContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  
  // Use a ref to store the keycloak instance
  const keycloakRef = useRef<Keycloak | null>(null);

  // Initialize Keycloak instance only once
  const getKeycloak = useCallback((): Keycloak => {
    if (!keycloakRef.current) {
      
      keycloakRef.current = new Keycloak({
        url: KEYCLOAK_URL,
        realm: KEYCLOAK_REALM,
        clientId: KEYCLOAK_CLIENT_ID
      });
      
      // Make keycloak available globally for other components
      (window as any).Keycloak = keycloakRef.current;
    }
    return keycloakRef.current;
  }, []);

  // Load user profile after authentication
  const loadUserProfile = useCallback(async () => {
    const keycloak = getKeycloak();
    
    if (!keycloak.authenticated) {
      
      return;
    }
    
    try {
      
      const profile = await keycloak.loadUserProfile();
      
      
      // Get user roles - properly extract from realmAccess
      let roles: string[] = [];
      
      // Extract roles from realmAccess if available
      if (keycloak.realmAccess && keycloak.realmAccess.roles) {
        roles = keycloak.realmAccess.roles;
        
      }
      
      // Also check resourceAccess for client roles
      if (keycloak.resourceAccess) {
        const clientId = KEYCLOAK_CLIENT_ID;
        if (keycloak.resourceAccess[clientId] && keycloak.resourceAccess[clientId].roles) {
          const clientRoles = keycloak.resourceAccess[clientId].roles;
          
          roles = [...roles, ...clientRoles];
        }
      }
      
      
      
      setUser({
        ...profile,
        sub: keycloak.subject,
        email: profile.email,
        name: profile.firstName + ' ' + profile.lastName,
        roles: roles,
      });
      
      setIsAuthenticated(true);
      
      // Sync with database and chat service
      await syncUserWithDatabase();
    } catch (error) {
      console.error('Failed to load user profile:', error);
      toast({
        title: "Profile Error",
        description: "Could not load your profile. Please try again.",
        variant: "destructive"
      });
    }
  }, [getKeycloak]);

  // Setup token refresh
  const setupTokenRefresh = useCallback(() => {
    const keycloak = getKeycloak();
    
    if (keycloak.authenticated) {
      // Setup token refresh
      const updateTokenInterval = setInterval(() => {
        keycloak.updateToken(70)
          .then(refreshed => {
            if (refreshed) {
              
            }
          })
          .catch(error => {
            console.error('Failed to refresh token', error);
            clearInterval(updateTokenInterval);
            signOut();
          });
      }, 60000); // Check token every minute
      
      // Clear interval on unmount
      return () => clearInterval(updateTokenInterval);
    }
    
    return undefined;
  }, [getKeycloak]);

  // Sync user with database
  const syncUserWithDatabase = async () => {
    const keycloak = getKeycloak();
    
    if (isAuthenticated && user && user.email && keycloak.token) {
      try {
        
        
        
        // Sync with database via API
        const response = await axios.post('/api/users/sync', { 
          userId: user.sub,
          email: user.email,
          name: user.name || user.email.split('@')[0],
          roles: Array.isArray(user.roles) ? user.roles : []
        }, {
          headers: {
            Authorization: `Bearer ${keycloak.token}`
          }
        });
        
        
        
        toast({
          title: "登录成功",
          description: "欢迎回来！",
        });
      } catch (error) {
        console.error('Failed to sync with database:', error);
      }
    }
  };
  
  // Handle authentication messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      
      
      // Accept messages from our own origin or auth.nisoo.com
      const allowedOrigins = [
        window.location.origin, 
        'https://auth.nisoo.com'
      ];
      
      if (!allowedOrigins.includes(event.origin)) {
        
        return;
      }

      // Process the URL from the silent-check-sso
      if (typeof event.data === 'string' && event.data.includes('code=')) {
        
        // The adapter will handle this automatically via the iframe
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Initialize Keycloak
  useEffect(() => {
    
    setIsLoading(true);
    
    const keycloak = getKeycloak();
    
    const initOptions: Keycloak.KeycloakInitOptions = {
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      redirectUri: KEYCLOAK_REDIRECT_URI,
      enableLogging: true,
      checkLoginIframe: true,
      flow: 'standard',
      pkceMethod: 'S256'
    };
    
    
    
    keycloak.init(initOptions)
      .then(authenticated => {
        if (authenticated) {
          loadUserProfile()
            .finally(() => {
              setIsLoading(false);
            });
          return setupTokenRefresh();
        } else {
          setIsAuthenticated(false);
          setUser(null);
          setIsLoading(false);
        }
      })
      .catch(error => {
        console.error('Failed to initialize Keycloak:', error);
        setIsAuthenticated(false);
        setUser(null);
        setIsLoading(false);
        toast({
          title: "Authentication Error",
          description: "Failed to initialize authentication. Please try again.",
          variant: "destructive"
        });
      });
      
    // Add event listeners
    keycloak.onAuthSuccess = () => {
      
      loadUserProfile();
    };
    
    keycloak.onAuthError = (error) => {
      console.error('Authentication error:', error);
    };
    
    keycloak.onTokenExpired = () => {
      
      keycloak.updateToken(70)
        .catch(() => {
          
          signOut();
        });
    };
    
    return () => {
      // Cleanup function will be returned by setupTokenRefresh if Keycloak was authenticated
    };
  }, [getKeycloak, loadUserProfile, setupTokenRefresh]);

  // Login function
  const signIn = useCallback(() => {
    
    setIsLoading(true);
    
    const keycloak = getKeycloak();
    
    keycloak.login({
      redirectUri: KEYCLOAK_REDIRECT_URI
    }).catch(error => {
      console.error('Login failed:', error);
      setIsLoading(false);
      toast({
        title: "Login Error",
        description: "Failed to initialize login. Please try again.",
        variant: "destructive"
      });
    });
  }, [getKeycloak]);

  // Register function
  const signUp = useCallback(() => {
    
    setIsLoading(true);
    
    const keycloak = getKeycloak();
    
    keycloak.register({
      redirectUri: KEYCLOAK_REDIRECT_URI
    }).catch(error => {
      console.error('Registration failed:', error);
      setIsLoading(false);
      toast({
        title: "Registration Error",
        description: "Failed to initialize registration. Please try again.",
        variant: "destructive"
      });
    });
  }, [getKeycloak]);

  // Logout function
  const signOut = useCallback(async () => {
    try {
      
      setIsLoading(true);
      
      // First try to sign out from chat API
      try {
        
        await axios.post(`${CHAT_API_URL}/auths/signout`, {}, {
          withCredentials: true
        });
        
      } catch (chatError) {
        console.error('Error signing out from chat API:', chatError);
        // Continue with Keycloak logout even if chat logout fails
      }
      
      // Clear any cached chat session cookies
      document.cookie = 'chat_session=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=None; Secure';
      
      const keycloak = getKeycloak();
      
      
      
      // Prepare for logout by clearing state first
      setUser(null);
      setIsAuthenticated(false);
      
      // Then logout from Keycloak
      await keycloak.logout({
        redirectUri: KEYCLOAK_REDIRECT_URI
      });
      
      
      
      toast({
        title: "已登出",
        description: "您已成功退出登录。",
      });
    } catch (error) {
      console.error('Error signing out:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [getKeycloak]);


  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(() => ({
    user,
    isLoading,
    isAuthenticated,
    signIn,
    signUp,
    signOut,
  }), [user, isLoading, isAuthenticated, signIn, signUp, signOut]);

  return (
    <KeycloakContext.Provider value={contextValue}>
      {children}
    </KeycloakContext.Provider>
  );
};

export const useKeycloakContext = () => {
  const context = useContext(KeycloakContext);
  if (context === undefined) {
    throw new Error('useKeycloakContext must be used within an KeycloakContextProvider');
  }
  return context;
};
